import { inputClass } from '../../common/Field'
import { useWizard } from '../../../context/WizardContext'
import { PAYMENT_METHODS } from '../../../utils/constants'
import { FIELDS, PAYMENT_COPY } from '../../../utils/copy'

/**
 * Step 8 — how the goods settle, and one thing that follows from it.
 *
 * **Two answers, not eight.** This listed GCash, Maya, MariBank, GrabPay, ShopeePay, bank
 * transfer and card, and the server mapped every one of them to the same thing. Machi
 * Express does not process payments — the customer settles with the partner directly —
 * and naming a wallet implied the app would handle it, which it never did.
 *
 * The delivery-fee tick is a second question, not a third method: `paymentMethod` says how
 * the *goods* settled, this says whether the *ride* did, and they vary independently. It
 * appears only on an online order, because nothing has been paid on a cash one.
 */
export default function PaymentStep() {
  const { state, errors, setField } = useWizard()
  const online = state.paymentMethod !== 'cash_on_delivery'

  return (
    <div className="grid gap-4">
      <div role="radiogroup" aria-label={FIELDS.paymentMethod} className="grid gap-2.5">
        {PAYMENT_METHODS.map((method) => {
          const copy = PAYMENT_COPY[method.value] ?? { label: method.label }
          const chosen = state.paymentMethod === method.value

          return (
            <button
              key={method.value}
              type="button"
              role="radio"
              aria-checked={chosen}
              onClick={() => setField('paymentMethod', method.value)}
              className={`text-left rounded-card border px-4 py-3.5 transition-colors duration-quick ease-out
                          ${chosen ? 'border-brand bg-blush' : 'border-edge bg-surface hover:border-brand'}`}
            >
              <span className={`block text-body font-semibold ${chosen ? 'text-brand' : 'text-ink'}`}>
                {copy.label}
              </span>
            </button>
          )
        })}
      </div>

      {errors.paymentMethod && (
        <p role="alert" className="text-body-s text-alert">
          {errors.paymentMethod}
        </p>
      )}

      {/* Only on an online order. Ticked on a cash one it would tell the rider to collect
          nothing, on an order where nothing has been paid — and `toOrderBody` forces it
          back to false for exactly that reason, so this is the visible half of that rule. */}
      {online && (
        <label className="flex items-start gap-3 rounded-card border border-edge bg-surface px-4 py-3.5 cursor-pointer">
          <input
            type="checkbox"
            checked={state.deliveryFeePaid}
            onChange={(event) => setField('deliveryFeePaid', event.target.checked)}
            className="mt-0.5 w-5 h-5 flex-none accent-[var(--brand)]"
          />
          <span className="text-body text-ink">{FIELDS.deliveryFeePaid}</span>
        </label>
      )}

      <div>
        <label
          htmlFor="specialInstructions"
          className="block text-label font-semibold uppercase text-ink-2 mb-1.5"
        >
          {FIELDS.notes}
        </label>

        <textarea
          id="specialInstructions"
          rows={3}
          maxLength={500}
          value={state.specialInstructions}
          onChange={(event) => setField('specialInstructions', event.target.value)}
          placeholder={FIELDS.notesHint}
          className={`${inputClass(false)} resize-y`}
        />

        {/* Counted down rather than up, and only near the end. A counter sitting at
            "0 / 500" from the first keystroke is a limit announced to somebody who was
            never going to reach it. */}
        {state.specialInstructions.length > 400 && (
          <p className="text-body-s text-ink-3 mt-[5px] tabular-nums">
            {500 - state.specialInstructions.length} na letra ang natitira.
          </p>
        )}
      </div>
    </div>
  )
}
